import { createFileRoute, Link } from '@tanstack/react-router';
import { FileSpreadsheet, Lock } from 'lucide-react';
import { DataNumber, DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { PageHeader } from '../components/layout/app-shell';
import { useSheets } from '../lib/sheets';

// Imported Actor Profile Sheets (task 12b), newest first, with the versions of each ID code.

export const Route = createFileRoute('/sheets/')({ component: SheetsPage });

function SheetsPage() {
  const sheets = useSheets();
  const rows = sheets.data?.sheets ?? [];
  return (
    <div className="space-y-6">
      <PageHeader title="Profile sheets" description="Actor Profile Sheets read with the template, newest first." />
      {sheets.isSuccess && rows.length === 0 && (
        <EmptyState icon={FileSpreadsheet} title="No profile sheets yet" learnMore="import-a-profile-sheet">
          Upload an Actor Profile Sheet (DOCX, PDF or Pages). It is recognised by its layout and read block by block.
        </EmptyState>
      )}
      {rows.length > 0 && (
        <div className="relative overflow-x-auto rounded-lg border bg-surface">
          <table className="w-full text-sm">
            <caption className="sr-only">Imported profile sheets</caption>
            <thead>
              <tr className="border-b text-muted-foreground">
                <th scope="col" className="p-2 text-start font-medium">
                  ID code
                </th>
                <th scope="col" className="p-2 text-start font-medium">
                  File
                </th>
                <th scope="col" className="p-2 text-start font-medium">
                  Version
                </th>
                <th scope="col" className="p-2 text-start font-medium">
                  Imported
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="p-2 font-medium">
                    <Link to="/sheets/$id" params={{ id: r.id }} className="underline-offset-2 hover:underline">
                      <DataText>{r.code ?? 'No ID code'}</DataText>
                    </Link>
                  </td>
                  <td className="p-2">
                    <span className="inline-flex items-center gap-1">
                      <DataText>{r.filename}</DataText>
                      {r.sensitivity === 'confidential' && (
                        <span title="Confidential: processed on Cloudflare only" className="inline-flex">
                          <Lock className="size-3 text-muted-foreground" aria-hidden="true" />
                          <span className="sr-only">Confidential</span>
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="p-2 text-xs">{r.supersedesDocumentId ? 'Update of an earlier sheet' : 'First import'}</td>
                  <td className="p-2 text-xs">
                    <DataNumber>
                      {new Date(r.createdAt).toLocaleString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </DataNumber>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
